import styled from 'styled-components';
import { FlexCenter, FlexColumn } from '@elementary/common-ui/components';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useViewLoadTracking from '@elementary/common-ui/hooks/useViewLoadTracking';
import { CircularProgress } from '@mui/material';
import {
  buildCustomSqlCloudEditInitialValues,
  toQualityDimension,
} from 'shared/components/AddTestWizard/customSqlCloudTest';
import { EditWizardState } from 'shared/components/AddTestWizard/types';
import PanelError from 'shared/components/PanelError';
import { EDITABLE_ASSET_TYPES } from 'shared/constants';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import { IntegrationType } from 'shared/types';
import { parseConditionalSeverity } from 'shared/utils/utils';
import { radius, space } from '@elementary/common-ui/style/utils';
import useTestConfiguration from '../../hooks/useTestConfiguration';
import { TestOverview } from '../../types';
import AnomalyTestConfiguration from './AnomalyTestConfiguration';
import CodeTestConfiguration from './CodeTestConfiguration';
import CustomSqlTestConfiguration from './CustomSqlTestConfiguration';
import DataContractTestConfiguration from './DataContractTestConfiguration';
import TestNotificationAccordion from './TestNotificationAccordion';

const resourceTypeMap: Record<string, 'tables' | 'columns'> = {
  TABLE: 'tables',
  COLUMN: 'columns',
};

const Wrapper = styled(FlexColumn)`
  background-color: ${({ theme }) => theme.palette.surface.soft};
  border-radius: ${radius('lg')};
  padding: ${space('lg', 'xxl')};
  gap: ${space('xxl')};
`;

type Props = {
  testOverview: TestOverview;
};

const TestConfiguration = ({ testOverview }: Props) => {
  const navigate = useNavigateWithEnv();
  const hasPermission = useEnvPermission();
  const hasEditPermission = hasPermission('EDIT_TEST_CONFIGURATIONS');

  const {
    testConfigData,
    testConditionalAlertDestinationsData,
    isTestConditionalAlertDestinationsLoading,
    isTestConfigLoading,
    isTestConfigFetching,
    muteTest,
    unMuteTest,
    isMuteTestLoading,
    testConfigError,
  } = useTestConfiguration({ testId: testOverview.execution?.testId });

  useViewLoadTracking({
    name: 'test configuration panel',
    loadDeps: [testConfigData],
    contextId: testOverview.execution?.testId || undefined,
  });

  const testConfig = testConfigData?.test?.config;

  const handleMuteTestChanged = (val: boolean) =>
    val ? unMuteTest() : muteTest();

  const allowEdit =
    testConfig?.type === 'yaml' &&
    EDITABLE_ASSET_TYPES.includes(
      testConfigData?.test.tableAsset?.tableType.toLowerCase() || '',
    );

  const resourceType =
    resourceTypeMap[
      testConfigData?.test?.resource?.type || resourceTypeMap.TABLE
    ];

  const handleEditClick = () => {
    if (!testConfigData?.test || testConfig?.type !== 'yaml') return;

    const state: EditWizardState = {
      wizardResourceType: resourceType,
      editTestId: testConfigData.test.id,
      editTestMetadata: {
        name: testConfigData.test.name,
        type: testConfigData.test.type,
        subType: testOverview.execution?.subType,
        resourceName: testConfigData.test.resource?.name,
        tableName: testConfigData.test.tableAsset.name,
        package: testConfigData.test.namespace,
      },
      initialValues: {
        resourceType,
        resources: [testConfigData.test.tableAsset.id || ''],
        yaml: testConfig.config,
        description: testConfig.description,
        owners: testConfig.owners,
        tags: testConfig.tags,
        severity: parseConditionalSeverity(
          testConfig.severity,
          testConfig.errorIf,
          testConfig.warnIf,
        ),
      },
    };

    navigate('/test-management/edit-test', { state });
  };

  const handleCustomSqlEditClick = () => {
    if (!testConfigData?.test || testConfig?.type !== 'custom_sql') return;

    const tableAsset = testConfigData.test.tableAsset;
    const state: EditWizardState = {
      wizardResourceType: 'customQuery',
      editTestId: testConfigData.test.id,
      editTestMetadata: {
        name: testConfig.name ?? testConfigData.test.name,
        type: testConfigData.test.type,
        tableName: tableAsset.name,
      },
      initialValues: buildCustomSqlCloudEditInitialValues({
        sql: testConfig.sql,
        name: testConfig.name ?? testConfigData.test.name,
        description: testOverview.test.description,
        schedule: testConfig.schedule,
        severity: testConfig.severity,
        failRowCountThreshold: testConfig.sql_config.fail_row_count_threshold,
        warnRowCountThreshold: testConfig.sql_config.warn_row_count_threshold,
        storeRowSample: testConfig.sql_config.store_row_sample,
        sampleRowLimit: testConfig.sql_config.sample_row_limit,
        queryTimeoutSeconds: testConfig.sql_config.query_timeout_seconds,
        qualityDimension: toQualityDimension(
          testOverview.execution.qualityDimension,
        ),
        owners: testOverview.test.owners,
        tags: testOverview.test.tags,
        primaryAsset: tableAsset
          ? { id: tableAsset.id, name: tableAsset.name }
          : null,
      }),
    };

    navigate('/test-management/edit-test', { state });
  };

  if (isTestConfigLoading) {
    return (
      <Wrapper>
        <FlexCenter sx={{ height: '100%', padding: space(0, 'xxl') }}>
          <CircularProgress size={32} />
        </FlexCenter>
      </Wrapper>
    );
  }

  if (testConfigError) {
    return (
      <PanelError
        error={testConfigError as Error}
        errorName="ApiError"
        page={'TestConfiguration'}
      />
    );
  }

  return (
    <Wrapper>
      {(testConfig?.type === 'yaml' ||
        testConfig?.type === 'query' ||
        testConfig?.type === 'source_freshness') && (
        <CodeTestConfiguration
          testConfig={testConfig}
          onEdit={allowEdit ? handleEditClick : undefined}
        />
      )}

      {(testConfig?.type === 'anomaly_detection' ||
        testConfig?.type === 'freshness_anomaly_detection' ||
        testConfig?.type === 'pipeline_task_performance') && (
        <AnomalyTestConfiguration testOverview={testOverview} />
      )}

      {testConfig?.type === 'data_contract' && (
        <DataContractTestConfiguration testOverview={testOverview} />
      )}

      {testConfig?.type === 'custom_sql' && (
        <CustomSqlTestConfiguration
          testConfig={testConfig}
          onEdit={hasEditPermission ? handleCustomSqlEditClick : undefined}
          testedTable={
            testOverview.test.tableAsset?.name ||
            testConfigData?.test.tableAsset.name
          }
          qualityDimension={toQualityDimension(
            testOverview.execution.qualityDimension,
          )}
        />
      )}

      <TestNotificationAccordion
        isMuted={!!testConfig?.muted}
        onChange={handleMuteTestChanged}
        isLoading={
          isMuteTestLoading ||
          isTestConfigFetching ||
          isTestConditionalAlertDestinationsLoading
        }
        latestExecutionTestId={testOverview.execution?.testId}
        alertDestinations={testConditionalAlertDestinationsData?.test?.conditionalAlertDestinations.map(
          (d) => ({
            ...d,
            type: d.type.toLowerCase() as IntegrationType,
          }),
        )}
      />
    </Wrapper>
  );
};

export default TestConfiguration;
